import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ScanLine,
  Search,
  Wifi,
  WifiOff,
  Battery,
  ChevronLeft,
  RefreshCw,
  Printer,
  AlertCircle,
  Check,
  Clock,
  X,
  User,
  ChevronDown,
  ChevronUp,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { useEventMeta } from "@/lib/event-meta";
import { API_BASE } from "@/lib/utils";
import { formatDualClock, useSyncedClock } from "@/lib/synced-clock";
import QrCameraScanner from "@/components/QrCameraScanner";

const GATE_PRESETS = ["Gate A", "Gate B", "Gate C"];

function onsiteStorageKey(eventId) {
  return `aivoryems.onsite.v1.${eventId}`;
}

function onsiteQueueKey(eventId) {
  return `aivoryems.onsite.queue.v1.${eventId}`;
}

export function loadQueue(eventId) {
  if (!eventId || typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(onsiteQueueKey(eventId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueue(eventId, items) {
  if (!eventId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(onsiteQueueKey(eventId), JSON.stringify(items));
  } catch {
    /* quota exceeded — silent; queue length will reflect what is actually persisted on next load */
  }
}

function makeLocalId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function enqueueScan(eventId, item) {
  const items = loadQueue(eventId);
  items.push({ localId: makeLocalId(), enqueuedAt: Date.now(), ...item });
  saveQueue(eventId, items);
  return items.length;
}

/** When event has staff rows, operator + PIN are required (server enforces). */
export function validateDeskState(staff, operatorId, operatorPin) {
  if (!Array.isArray(staff) || staff.length === 0) {
    return { ok: false, message: "Add staff with PINs in Attendees before onsite check-in." };
  }
  if (!operatorId) {
    return { ok: false, message: "Select an operator before scanning." };
  }
  const op = staff.find((s) => s.id === operatorId);
  if (!op) {
    return { ok: false, message: "Selected operator is no longer valid." };
  }
  if (!(operatorPin || "").trim()) {
    return { ok: false, message: "Enter the operator PIN." };
  }
  return { ok: true };
}

export function addStaffToBody(body, operatorId, operatorPin) {
  if (operatorId) {
    body.staff_id = operatorId;
    if ((operatorPin || "").trim()) body.staff_pin = operatorPin.trim();
  }
  return body;
}

function initials(name) {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function isAttendeeCheckedIn(attendee) {
  if (!attendee || typeof attendee !== "object") return false;
  if (attendee.checkin) return true;
  return String(attendee.status || "").trim().toLowerCase() === "checked in";
}

function seatFromAttendee(attendee) {
  return String(attendee?.seat || "").trim();
}

function showSeatPopupFromAttendee(setSeatPopup, attendee, alreadyIn = false) {
  const seat = seatFromAttendee(attendee);
  if (!seat) return;
  setSeatPopup({
    name: String(attendee?.name || "Attendee").trim() || "Attendee",
    company: String(attendee?.company || "").trim(),
    ticket: String(attendee?.ticket || "").trim(),
    seat,
    alreadyIn: Boolean(alreadyIn),
  });
}

function SeatCheckinPopup({ popup, onDismiss }) {
  useEffect(() => {
    if (!popup?.seat) return undefined;
    const timer = window.setTimeout(onDismiss, 5000);
    return () => window.clearTimeout(timer);
  }, [popup, onDismiss]);

  if (!popup?.seat) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-8 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="seat-popup-title"
      data-testid="seat-checkin-popup"
    >
      <div className="surface w-full max-w-lg border border-[var(--primary)]/50 shadow-2xl p-6 md:p-8 text-center relative fade-up">
        <button
          type="button"
          className="absolute top-3 right-3 p-2 rounded-md text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elev)] transition onsite-touch-btn"
          onClick={onDismiss}
          aria-label="Dismiss seat assignment"
          data-testid="seat-popup-dismiss"
        >
          <X size={20} />
        </button>
        <div className="text-overline text-[var(--primary)]">
          {popup.alreadyIn ? "Already checked in" : "Checked in"}
        </div>
        <h2 id="seat-popup-title" className="font-heading text-2xl md:text-3xl mt-3 tracking-tight">
          {popup.name}
        </h2>
        {(popup.company || popup.ticket) && (
          <p className="text-sm font-mono text-[var(--text-secondary)] mt-2">
            {[popup.company, popup.ticket].filter(Boolean).join(" · ")}
          </p>
        )}
        <div className="mt-6 md:mt-8 py-6 md:py-8 border-y border-[var(--border-default)]">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Assigned seat</div>
          <div className="font-heading onsite-seat-popup-number mt-3 text-[var(--primary)] tracking-tight">{popup.seat}</div>
        </div>
        <button type="button" className="btn-primary w-full justify-center mt-6 onsite-touch-btn min-h-[48px]" onClick={onDismiss}>
          Dismiss
        </button>
        <p className="text-[11px] font-mono text-[var(--text-tertiary)] mt-3">Auto-closes in 5 seconds</p>
      </div>
    </div>
  );
}

export default function Onsite() {
  const { mode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventParam = searchParams.get("event") || "evt_001";
  const gateLabel = (searchParams.get("gate") || "Gate A").trim() || "Gate A";
  const eventMeta = useEventMeta(eventParam);
  const resolvedEventId = eventMeta.id || eventParam;

  const [active, setActive] = useState(mode === "manual" ? "manual" : mode === "scan" ? "scan" : "home");
  const [browserOnline, setBrowserOnline] = useState(() =>
    typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" ? navigator.onLine : true
  );
  const [manualOffline, setManualOffline] = useState(false);
  const online = browserOnline && !manualOffline;
  const [tick, setTick] = useState(0);
  const [scan, setScan] = useState(null);
  const [seatPopup, setSeatPopup] = useState(null);
  const dismissSeatPopup = useCallback(() => setSeatPopup(null), []);
  const [queueLen, setQueueLen] = useState(0);
  const drainingRef = useRef(false);
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [operatorId, setOperatorId] = useState("");
  const [operatorPin, setOperatorPin] = useState("");
  const [deskHelpOpen, setDeskHelpOpen] = useState(false);

  const setGateParam = useCallback(
    (gate) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("gate", gate);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!resolvedEventId) return;
    try {
      const raw = sessionStorage.getItem(onsiteStorageKey(resolvedEventId));
      if (raw) {
        const j = JSON.parse(raw);
        if (j.operatorId && typeof j.operatorId === "string") setOperatorId(j.operatorId);
      }
    } catch {
      /* ignore */
    }
  }, [resolvedEventId]);

  useEffect(() => {
    if (!resolvedEventId) return;
    try {
      sessionStorage.setItem(onsiteStorageKey(resolvedEventId), JSON.stringify({ operatorId }));
    } catch {
      /* ignore */
    }
  }, [resolvedEventId, operatorId]);

  useEffect(() => {
    if (!resolvedEventId) return;
    let cancelled = false;
    (async () => {
      setStaffLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/events/${resolvedEventId}/staff`);
        if (!res.ok) throw new Error("staff");
        const data = await res.json();
        if (!cancelled) setStaff(Array.isArray(data.staff) ? data.staff : []);
      } catch {
        if (!cancelled) setStaff([]);
      } finally {
        if (!cancelled) setStaffLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedEventId]);

  const loadDashboard = useCallback(async () => {
    if (!resolvedEventId) return;
    setDashLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/events/${resolvedEventId}/live-checkin-dashboard`);
      if (!res.ok) throw new Error("dashboard");
      const data = await res.json();
      setDashboard(data);
    } catch {
      setDashboard(null);
    } finally {
      setDashLoading(false);
    }
  }, [resolvedEventId]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onOn = () => setBrowserOnline(true);
    const onOff = () => setBrowserOnline(false);
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    return () => {
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
    };
  }, []);

  useEffect(() => {
    setQueueLen(loadQueue(resolvedEventId).length);
  }, [resolvedEventId]);

  const drainQueue = useCallback(async () => {
    if (drainingRef.current) return;
    if (!resolvedEventId) return;
    let items = loadQueue(resolvedEventId);
    if (items.length === 0) return;
    drainingRef.current = true;
    const initialLen = items.length;
    try {
      while (items.length > 0) {
        const next = items[0];
        let res;
        try {
          res = await fetch(`${API_BASE}${next.url}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(next.body),
          });
        } catch {
          // network failure — keep queue intact, retry on next online flip or next submit
          return;
        }
        if (res.status >= 500) {
          // server-side transient — same behavior as network failure
          return;
        }
        // 2xx success or 4xx terminal (404 missing, 409 already-in, 400/403 not retriable)
        // are both removed from the queue. 4xx surfaces as a toast so the operator knows.
        if (!res.ok) {
          let detail = res.statusText;
          try {
            const j = await res.json();
            detail = j.detail || detail;
          } catch {
            /* ignore */
          }
          if (res.status === 409) {
            toast.message(`Already checked in (queued scan)`);
          } else {
            toast.error(`Queued scan dropped: ${detail}`);
          }
        }
        items = items.slice(1);
        saveQueue(resolvedEventId, items);
        setQueueLen(items.length);
      }
      if (initialLen > 0) {
        toast.success(`Offline queue synced (${initialLen})`);
        loadDashboard();
      }
    } finally {
      drainingRef.current = false;
    }
  }, [resolvedEventId, loadDashboard]);

  useEffect(() => {
    if (!online) return;
    drainQueue();
  }, [online, drainQueue]);

  const syncAll = useCallback(async () => {
    await drainQueue();
    await loadDashboard();
  }, [drainQueue, loadDashboard]);

  const submitCheckin = useCallback(
    async ({ endpoint, body }) => {
      if (!resolvedEventId) return { ok: false, error: "No event selected" };
      const url = `/api/events/${resolvedEventId}/${endpoint}`;
      const scannedAt = new Date().toISOString();
      const fullBody = { ...body, scanned_at: scannedAt };
      if (!online) {
        const len = enqueueScan(resolvedEventId, { url, body: fullBody, scannedAt });
        setQueueLen(len);
        return { queued: true, scannedAt };
      }
      try {
        const res = await fetch(`${API_BASE}${url}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fullBody),
        });
        if (res.status >= 500) {
          const len = enqueueScan(resolvedEventId, { url, body: fullBody, scannedAt });
          setQueueLen(len);
          setTimeout(() => drainQueue(), 1500);
          return { queued: true, transient: true, scannedAt };
        }
        if (!res.ok) {
          let detail = res.statusText;
          try {
            const j = await res.json();
            detail = j.detail || detail;
          } catch {
            /* ignore */
          }
          return { ok: false, status: res.status, error: detail };
        }
        const data = await res.json();
        if (queueLen > 0) setTimeout(() => drainQueue(), 0);
        return { ok: true, data };
      } catch {
        const len = enqueueScan(resolvedEventId, { url, body: fullBody, scannedAt });
        setQueueLen(len);
        return { queued: true, transient: true, scannedAt };
      }
    },
    [online, resolvedEventId, drainQueue, queueLen]
  );

  useEffect(() => {
    if (mode === "scan") setActive("scan");
    else if (mode === "manual") setActive("manual");
  }, [mode]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (tick % 10 !== 0) return;
    loadDashboard();
  }, [tick, loadDashboard]);

  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, []);

  const eventTimeZone = dashboard?.timeZone || "Asia/Singapore";
  const syncedNow = useSyncedClock(dashboard?.serverTime);
  const clock = formatDualClock(syncedNow, eventTimeZone);

  const headerTitle = (eventMeta.title || "Event").toUpperCase();
  const operatorRecord = staff.find((s) => s.id === operatorId);
  const operatorDisplay = operatorRecord ? operatorRecord.badge_label || operatorRecord.name : "";
  const deskReady = validateDeskState(staff, operatorId, operatorPin).ok;
  const operatorLinked = Boolean(operatorId && (!operatorRecord?.has_pin || (operatorPin || "").trim()));

  const scanDeskMode = mode === "scan" || active === "scan";

  useEffect(() => {
    document.documentElement.classList.add("onsite-kiosk");
    return () => document.documentElement.classList.remove("onsite-kiosk");
  }, []);

  return (
    <div className="onsite-app min-h-screen text-[var(--text-primary)]" data-testid="onsite-app">
      {/* Status bar */}
      <div className="onsite-app__safe-top border-b border-[var(--border-default)] min-h-12 px-4 md:px-6 onsite-app__safe-x flex items-center justify-between gap-3 text-[11px] font-mono uppercase tracking-widest">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin" className="btn-ghost px-0 text-xs shrink-0">
            <ChevronLeft size={12} />
          </Link>
          <span className="flex items-center gap-1.5 text-[var(--primary)] min-w-0">
            <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full pulse-dot shrink-0" />
            <span className="truncate">
              ONSITE · {gateLabel} · {headerTitle}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-5 text-[var(--text-secondary)] shrink-0">
          <button
            type="button"
            onClick={() => {
              if (!browserOnline) return;
              if (manualOffline) {
                // Resuming online is non-destructive — single tap clears the gate
                // and lets the queue drain.
                setManualOffline(false);
                toast.success("Resumed online — queue will drain.");
                return;
              }
              // Going online → forced offline IS destructive: subsequent scans
              // get queued instead of submitted. Confirm first so a stray tap
              // doesn't silently change check-in semantics mid-shift.
              const ok = typeof window !== "undefined" && window.confirm(
                "Force this desk into offline mode? Scans will be queued locally and replayed when you resume."
              );
              if (ok) {
                setManualOffline(true);
                toast.message("Desk is now in forced-offline mode.");
              }
            }}
            disabled={!browserOnline}
            className="flex items-center gap-1.5 px-2 py-1 min-h-[36px] hover:text-[var(--text-primary)] disabled:cursor-not-allowed"
            title={
              !browserOnline
                ? "Browser reports no network"
                : manualOffline
                  ? "Forced offline. Tap to resume."
                  : "Tap to force offline (confirmation required)"
            }
            data-testid="toggle-online"
          >
            {online ? (
              <>
                <Wifi size={11} className="text-[var(--primary)]" /> ONLINE
                {queueLen > 0 ? <span className="text-[#F59E0B]"> · QUEUE {queueLen}</span> : null}
              </>
            ) : (
              <>
                <WifiOff size={11} className="text-[var(--error)]" /> OFFLINE
                {queueLen > 0 ? ` · QUEUE ${queueLen}` : ""}
              </>
            )}
          </button>
          <span className="hidden sm:flex items-center gap-1.5 text-[var(--text-tertiary)]" title="Device battery not reported">
            <Battery size={11} /> —
          </span>
          <span>{clock}</span>
        </div>
      </div>

      {/* Desk identity — gate + operator (audit) */}
      <DeskStrip
        eventTitle={eventMeta.title || "Event"}
        eventIdParam={eventParam}
        resolvedEventId={resolvedEventId}
        gateLabel={gateLabel}
        setGateParam={setGateParam}
        staff={staff}
        staffLoading={staffLoading}
        operatorId={operatorId}
        setOperatorId={(id) => {
          setOperatorId(id);
          setOperatorPin("");
        }}
        operatorPin={operatorPin}
        setOperatorPin={setOperatorPin}
        deskReady={deskReady}
        needsOperator={false}
        deskHelpOpen={deskHelpOpen}
        setDeskHelpOpen={setDeskHelpOpen}
        compact={scanDeskMode}
      />

      {!operatorLinked && staff.length > 0 ? (
        <div className="px-4 md:px-6 py-2 bg-[var(--border-default)]/40 border-b border-[var(--border-default)] text-xs text-[var(--text-secondary)] flex items-center gap-2">
          <AlertCircle size={14} className="shrink-0" />
          <span>
            Operator is optional. Select one only if you want scans attributed to a specific usher.
          </span>
        </div>
      ) : null}

      {/* Mode tabs */}
      <div className="border-b border-[var(--border-default)] px-4 md:px-6 onsite-app__safe-x flex overflow-x-auto scrollbar-none">
        {[
          ["home", "Home"],
          ["scan", "Scan"],
          ["manual", "Manual search"],
          ["session", "Session check-in"],
          ["reprint", "Reprint"],
        ].map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setActive(k)}
            data-testid={`onsite-tab-${k}`}
            aria-current={active === k ? "page" : undefined}
            className={`shrink-0 min-h-[48px] px-4 md:px-5 py-3 text-xs font-mono uppercase tracking-widest border-b-2 transition onsite-touch-btn ${
              active === k ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            } ${scanDeskMode && k === "scan" ? "font-semibold" : ""}`}
          >
            {l}
          </button>
        ))}
        <div className="ml-auto flex items-center pr-2 gap-2">
          <span
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
              operatorLinked ? "border-[var(--primary)]/50 text-[var(--primary)]" : "border-[#71717A]/50 text-[var(--text-tertiary)]"
            }`}
            title="Operator attribution is optional"
          >
            {operatorLinked ? "Operator linked" : "Operator optional"}
          </span>
        </div>
      </div>

      {/* Body */}
      <div
        className={`p-4 md:p-6 lg:p-8 onsite-app__safe-x onsite-app__safe-bottom grid gap-4 md:gap-6 ${
          scanDeskMode ? "onsite-scan-desk" : "grid-cols-1 md:grid-cols-12"
        }`}
      >
        <main className={scanDeskMode ? "onsite-scan-main min-w-0" : "col-span-1 md:col-span-8"}>
          {active === "home" && <Home setActive={setActive} gateLabel={gateLabel} deskReady={operatorLinked} />}
          {active === "scan" && (
            <Scan
              setScan={setScan}
              setSeatPopup={setSeatPopup}
              scan={scan}
              gateLabel={gateLabel}
              staff={staff}
              operatorId={operatorId}
              operatorPin={operatorPin}
              setOperatorPin={setOperatorPin}
              submitCheckin={submitCheckin}
              onSync={syncAll}
            />
          )}
          {active === "manual" && (
            <Manual
              setScan={setScan}
              setSeatPopup={setSeatPopup}
              eventId={resolvedEventId}
              gateLabel={gateLabel}
              staff={staff}
              operatorId={operatorId}
              operatorPin={operatorPin}
              setOperatorPin={setOperatorPin}
              submitCheckin={submitCheckin}
              onSync={syncAll}
            />
          )}
          {active === "session" && <Session eventId={resolvedEventId} setActive={setActive} />}
          {active === "reprint" && <Reprint />}
        </main>

        <aside className={scanDeskMode ? "onsite-scan-aside min-w-0" : "col-span-1 md:col-span-4 space-y-6"}>
          <SidePanel
            dashboard={dashboard}
            loading={dashLoading}
            onRefresh={loadDashboard}
            gateLabel={gateLabel}
            operatorDisplay={operatorDisplay}
            deskReady={operatorLinked}
            staffCount={staff.length}
          />
        </aside>
      </div>
      <SeatCheckinPopup popup={seatPopup} onDismiss={dismissSeatPopup} />
    </div>
  );
}

function DeskStrip({
  eventTitle,
  eventIdParam,
  resolvedEventId,
  gateLabel,
  setGateParam,
  staff,
  staffLoading,
  operatorId,
  setOperatorId,
  operatorPin,
  setOperatorPin,
  deskReady,
  needsOperator,
  deskHelpOpen,
  setDeskHelpOpen,
  compact = false,
}) {
  const selected = staff.find((s) => s.id === operatorId);
  const showPin = Boolean(selected?.has_pin);

  return (
    <div className="border-b border-[var(--border-default)] bg-[var(--bg-surface)]">
      <div
        className={`px-4 md:px-6 onsite-app__safe-x ${compact ? "py-3" : "py-4"} flex flex-col xl:flex-row xl:items-end gap-3 xl:gap-6 max-w-[1600px] mx-auto`}
      >
        {!compact ? (
          <div className="flex-1 min-w-0">
            <div className="text-overline flex items-center gap-2">
              <User size={12} className="text-[var(--primary)]" />
              Desk setup
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              <span className="text-[var(--text-primary)] font-medium">{eventTitle}</span>
              <span className="text-[var(--text-tertiary)] font-mono text-xs ml-2">event={eventIdParam}</span>
            </p>
          </div>
        ) : (
          <div className="flex-1 min-w-0 text-xs font-mono text-[var(--text-tertiary)] truncate">
            {eventTitle} · {gateLabel}
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-overline">Gate</span>
            <select
              value={GATE_PRESETS.includes(gateLabel) ? gateLabel : "__custom__"}
              onChange={(ev) => {
                const v = ev.target.value;
                if (v === "__custom__") return;
                setGateParam(v);
              }}
              className={`input-dark mt-1 min-w-[120px] text-xs onsite-touch-btn ${compact ? "min-h-[44px] text-sm" : ""}`}
              data-testid="onsite-gate-select"
            >
              {GATE_PRESETS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
              {!GATE_PRESETS.includes(gateLabel) ? <option value="__custom__">Custom (from URL)…</option> : null}
            </select>
          </label>
          {!GATE_PRESETS.includes(gateLabel) && (
            <label className="block">
              <span className="text-overline">Custom gate</span>
              <input
                className="input-dark mt-1 font-mono text-xs min-w-[140px]"
                defaultValue={gateLabel}
                key={gateLabel}
                onBlur={(ev) => setGateParam(ev.target.value.trim() || "Gate A")}
                placeholder="Gate name"
              />
            </label>
          )}

          <label className="block min-w-[200px]">
            <span className="text-overline">Operator</span>
            <select
              value={operatorId}
              onChange={(ev) => setOperatorId(ev.target.value)}
              disabled={staffLoading}
              className={`input-dark mt-1 w-full text-xs onsite-touch-btn ${compact ? "min-h-[44px] text-sm" : ""}`}
              title="Staff list from Admin → Attendees → Staff"
              data-testid="onsite-operator-select"
            >
              <option value="">{staff.length ? "Who is scanning?" : "No staff yet (optional)"}</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.badge_label || s.name} · {s.role_type}
                  {s.has_pin ? " · PIN" : ""}
                </option>
              ))}
            </select>
          </label>

          {showPin ? (
            <label className="block">
              <span className="text-overline">PIN</span>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={operatorPin}
                onChange={(ev) => setOperatorPin(ev.target.value)}
                placeholder="Operator PIN"
                className="input-dark mt-1 font-mono text-xs max-w-[140px]"
                data-testid="onsite-operator-pin"
              />
            </label>
          ) : null}

          {!compact ? (
            <button
              type="button"
              className="btn-ghost text-xs flex items-center gap-1 mb-1"
              onClick={() => setDeskHelpOpen((o) => !o)}
              aria-expanded={deskHelpOpen}
            >
              <Info size={12} />
              How this works
              {deskHelpOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          ) : null}
        </div>
      </div>

      {deskHelpOpen ? (
        <div className="px-6 pb-4 max-w-[1600px] mx-auto">
          <div className="surface p-4 text-xs text-[var(--text-secondary)] leading-relaxed space-y-2 border border-[var(--border-default)]">
            <p>
              <strong className="text-[var(--text-primary)]">Gate</strong> is stored in the URL (<span className="font-mono">?gate=Gate%20A</span>) so each device can bookmark the correct
              entrance. Changing the dropdown updates the URL.
            </p>
            <p>
              <strong className="text-[var(--text-primary)]">Operator</strong> links scans to a staff profile (same list as Admin → Attendees → Staff). Your selection is remembered on this
              browser for event <span className="font-mono text-[var(--text-tertiary)]">{resolvedEventId}</span>. PINs are never shown on screen after entry; they are verified on the server.
            </p>
            <p className="text-[var(--text-tertiary)]">
              If this event has no staff rows, check-ins still work but are not tied to a named usher. Add ushers under Admin first for audit-grade shifts.
            </p>
          </div>
        </div>
      ) : null}

      {!deskReady && !needsOperator ? (
        <div className="px-6 pb-3 text-[11px] font-mono text-[var(--error)]">Enter operator PIN to continue.</div>
      ) : null}
    </div>
  );
}

function Home({ setActive, gateLabel, deskReady }) {
  return (
    <div>
      <div className="text-overline">Operator</div>
      <h1 className="font-heading text-5xl tracking-tight font-medium mt-2">Onsite desk · {gateLabel}</h1>
      <p className="text-[var(--text-secondary)] mt-3 text-sm max-w-xl">
        Confirm <strong className="text-[var(--text-primary)]">gate</strong> and <strong className="text-[var(--text-primary)]">operator</strong> in the desk strip above, then pick a mode. If Wi-Fi drops,
        scans are saved locally and replayed automatically when the connection returns; the status bar shows the pending queue.
      </p>
      {!deskReady ? (
        <p className="text-xs font-mono text-[var(--text-secondary)] mt-2">You can scan now. Select operator only when you want usher attribution.</p>
      ) : null}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
        {[
          [ScanLine, "Scan", "Use the device camera or keyboard wedge to scan badge QR codes.", "scan", true],
          [Search, "Manual search", "Search attendees, then check in with one tap.", "manual"],
          [User, "Session check-in", "View session capacity from your published schedule.", "session"],
          [Printer, "Reprint badge", "Reprint queue when tracked in the system.", "reprint"],
        ].map(([I, t, d, k, accent]) => (
          <button
            key={k}
            type="button"
            onClick={() => setActive(k)}
            data-testid={`mode-${k}`}
            className={`bg-[var(--bg-elev)] surface-hover text-left p-8 group ${accent ? "ring-1 ring-[var(--primary)]/40" : ""}`}
          >
            <I size={28} strokeWidth={1.4} className={accent ? "text-[var(--primary)]" : "text-[var(--text-primary)]"} />
            <div className="font-heading text-2xl mt-5">{t}</div>
            <div className="text-sm text-[var(--text-secondary)] mt-2">{d}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Scan({ setScan, setSeatPopup, scan, gateLabel, staff, operatorId, operatorPin, setOperatorPin, submitCheckin, onSync }) {
  const [testCode, setTestCode] = useState("");
  const [syncing, setSyncing] = useState(false);
  const scanningRef = useRef(false);

  const runCheckin = useCallback(
    async (rawCode) => {
      const code = String(rawCode || "").trim();
      if (!code) {
        setScan({ ok: false, reason: "Enter attendee id, email, or badge QR payload" });
        setTimeout(() => setScan(null), 1800);
        return;
      }
      if (scanningRef.current) return;
      scanningRef.current = true;
      const gate = gateLabel || "Gate A";
      const v = validateDeskState(staff, operatorId, operatorPin);
      if (!v.ok) {
        toast.error(v.message || "Select operator and PIN before scanning.");
        scanningRef.current = false;
        return;
      }
      const isBadgeQr = code.startsWith("B1|");
      let body = isBadgeQr
        ? { code, gate }
        : code.includes("@")
          ? { email: code, gate }
          : { attendee_id: code, gate };
      body = addStaffToBody(body, operatorId, operatorPin);
      try {
        const result = await submitCheckin({ endpoint: isBadgeQr ? "badge/scan" : "checkin", body });
        if (result.queued) {
          setScan({ queued: true, code, transient: !!result.transient });
          setOperatorPin("");
          setTestCode("");
          toast.message(result.transient ? "Network unstable — queued for sync." : "Saved offline — will sync when back online.");
          setTimeout(() => setScan(null), 2200);
          return;
        }
        if (!result.ok) {
          setScan({ ok: false, reason: result.error });
          setTimeout(() => setScan(null), 2200);
          return;
        }
        const a = result.data?.attendee || {};
        const alreadyIn = Boolean(result.data?.alreadyCheckedIn);
        setScan({
          ok: true,
          alreadyIn,
          a: {
            name: a.name || "Attendee",
            company: a.company || "",
            ticket: a.ticket || "",
            seat: a.seat || "",
            email: a.email || "",
          },
        });
        showSeatPopupFromAttendee(setSeatPopup, a, alreadyIn);
        setOperatorPin("");
        setTestCode("");
        onSync?.();
        toast.message(
          alreadyIn
            ? `${a.name || "Attendee"} is already checked in`
            : `Checked in ${a.name || "attendee"}${a.seat ? ` · Seat ${a.seat}` : ""}`,
        );
        setTimeout(() => setScan(null), seatFromAttendee(a) ? 5000 : 2200);
      } finally {
        scanningRef.current = false;
      }
    },
    [gateLabel, staff, operatorId, operatorPin, submitCheckin, onSync, setScan, setSeatPopup, setOperatorPin]
  );

  const trigger = async (kind) => {
    if (kind !== "ok") {
      setScan(null);
      return;
    }
    await runCheckin(testCode);
  };

  const handleCameraScan = useCallback(
    (decoded) => {
      setTestCode(decoded);
      runCheckin(decoded);
    },
    [runCheckin]
  );

  const handleSync = async () => {
    setSyncing(true);
    try {
      await onSync?.();
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-overline">Scanner</div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl tracking-tight font-medium mt-1">Scan badge QR</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-xl">
            Point the iPad camera at the attendee badge. Keyboard wedge and paste still work below.
          </p>
        </div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--text-tertiary)] border border-[var(--border-default)] px-3 py-2">
          {gateLabel}
        </div>
      </div>

      <div
        className={`mt-4 md:mt-6 surface relative overflow-hidden onsite-scanner-viewport flex items-center justify-center ${scan?.ok ? "flash-success" : ""}`}
        style={{ background: "#000" }}
        data-testid="scanner-viewport"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,86,8,0.08),transparent_70%)] pointer-events-none z-10" />
        {["top-4 left-4 md:top-6 md:left-6 border-t-2 border-l-2", "top-4 right-4 md:top-6 md:right-6 border-t-2 border-r-2", "bottom-4 left-4 md:bottom-6 md:left-6 border-b-2 border-l-2", "bottom-4 right-4 md:bottom-6 md:right-6 border-b-2 border-r-2"].map(
          (c, i) => (
            <div key={i} className={`absolute w-12 h-12 md:w-16 md:h-16 border-[var(--primary)] z-20 pointer-events-none ${c}`} />
          )
        )}

        {!scan && (
          <div className="absolute inset-0 z-0">
            <QrCameraScanner active={!scan} onScan={handleCameraScan} className="h-full" />
          </div>
        )}

        {!scan && <div className="absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-[var(--primary)] to-transparent scan-line z-20 pointer-events-none" />}

        {!scan && (
          <div className="absolute bottom-4 inset-x-0 text-center z-20 pointer-events-none px-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/80 bg-black/50 inline-block px-3 py-1 rounded">
              Camera active · aim at badge QR
            </div>
            {!operatorId ? <div className="text-[10px] text-white/60 mt-2">Scanning without operator attribution.</div> : null}
          </div>
        )}

        {scan?.ok && (
          <div className="text-center relative fade-up z-30 px-4 max-w-2xl mx-auto">
            <Check size={64} strokeWidth={2} className="mx-auto text-[var(--primary)] md:w-20 md:h-20" />
            <div className="font-heading onsite-scan-success-name mt-5 md:mt-6 tracking-tight">{scan.a.name}</div>
            <div className="text-sm md:text-base font-mono text-[var(--text-secondary)] mt-2">
              {scan.a.company} · {scan.a.ticket}
            </div>
            {scan.a.seat ? (
              <div className="mt-4 font-heading onsite-scan-success-seat tracking-tight text-[var(--primary)]">Seat {scan.a.seat}</div>
            ) : null}
            <div className="mt-5 inline-block font-mono text-[11px] uppercase tracking-widest px-4 py-1.5 border border-[var(--primary)]/40 text-[var(--primary)]">
              {scan.alreadyIn ? "ALREADY CHECKED IN" : "CHECKED-IN"}
            </div>
          </div>
        )}

        {scan?.queued && (
          <div className="text-center relative fade-up px-6 z-30">
            <Clock size={72} strokeWidth={1.6} className="mx-auto text-[#F59E0B]" />
            <div className="font-heading text-3xl mt-6 tracking-tight">Queued offline</div>
            <div className="text-sm font-mono text-[var(--text-secondary)] mt-2 break-all max-w-md mx-auto">{scan.code}</div>
            <div className="mt-4 inline-block font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-[#F59E0B]/40 text-[#F59E0B]">
              {scan.transient ? "RETRY PENDING" : "WILL SYNC"}
            </div>
          </div>
        )}

        {scan && !scan.ok && !scan.queued && (
          <div className="text-center relative fade-up px-6 z-30">
            <X size={72} strokeWidth={2} className="mx-auto text-[#FF453A]" />
            <div className="font-heading text-3xl mt-6 tracking-tight">{scan.reason ? "Check-in failed" : "Could not check in"}</div>
            <div className="text-sm font-mono text-[var(--text-secondary)] mt-2">{scan.reason ? String(scan.reason) : "Try manual search"}</div>
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <label className="text-overline">Fallback input (keyboard wedge / paste)</label>
          <input
            value={testCode}
            onChange={(ev) => setTestCode(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") trigger("ok");
            }}
            placeholder="Attendee id, email, or badge QR (B1|...)"
            className="input-dark w-full font-mono text-sm mt-2 min-h-[48px] onsite-touch-btn"
            data-testid="onsite-scan-code"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
        <div className="flex flex-wrap gap-2 lg:flex-nowrap">
          <button type="button" onClick={() => trigger("ok")} className="btn-primary justify-center flex-1 lg:flex-none onsite-touch-btn min-w-[140px]" data-testid="simulate-ok">
            Check in
          </button>
          <button type="button" onClick={() => trigger("err")} className="btn-secondary justify-center text-[var(--error)] border-[var(--error)]/40 onsite-touch-btn" data-testid="simulate-err">
            Clear
          </button>
          <button type="button" className="btn-secondary justify-center onsite-touch-btn" onClick={handleSync} disabled={syncing}>
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "…" : "Sync"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Manual({ setScan, setSeatPopup, eventId, gateLabel, staff, operatorId, operatorPin, setOperatorPin, submitCheckin, onSync }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState("");

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const qTrim = q.trim();
    if (!qTrim) {
      setResults([]);
      setLoading(false);
      return;
    }
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ limit: "50", segment: "invitees" });
        qs.set("q", qTrim);
        const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees?${qs.toString()}`);
        if (!res.ok) throw new Error("load");
        const data = await res.json();
        if (!cancelled) setResults(Array.isArray(data.attendees) ? data.attendees : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [q, eventId]);

  const checkInAttendee = async (a) => {
    const v = validateDeskState(staff, operatorId, operatorPin);
    if (!v.ok) {
      toast.error(v.message || "Select operator and PIN before check-in.");
      return;
    }
    const gate = gateLabel || "Gate A";
    let body = { attendee_id: a.id, gate };
    body = addStaffToBody(body, operatorId, operatorPin);
    setSubmittingId(a.id);
    try {
      const result = await submitCheckin({ endpoint: "checkin", body });
      if (result.queued) {
        setOperatorPin("");
        setScan({ queued: true, code: a.email || a.id, transient: !!result.transient });
        toast.message(result.transient ? "Network unstable — queued for sync." : "Saved offline — will sync when back online.");
        setTimeout(() => setScan(null), 1800);
        return;
      }
      if (!result.ok) {
        toast.error(result.error || "Check-in failed");
        return;
      }
      const att = result.data?.attendee || a;
      const alreadyIn = Boolean(result.data?.alreadyCheckedIn);
      setResults((prev) =>
        prev.map((row) =>
          row.id === a.id
            ? { ...row, ...att, status: "Checked In", checkin: att.checkin || row.checkin || new Date().toISOString() }
            : row,
        ),
      );
      setOperatorPin("");
      const attendeeView = {
        name: att.name || a.name || "Attendee",
        company: att.company || a.company || "",
        ticket: att.ticket || a.ticket || "",
        seat: att.seat || a.seat || "",
        email: att.email || a.email || "",
      };
      setScan({
        ok: true,
        alreadyIn,
        a: attendeeView,
      });
      showSeatPopupFromAttendee(setSeatPopup, attendeeView, alreadyIn);
      onSync?.();
      toast.message(
        alreadyIn
          ? `${attendeeView.name} is already checked in`
          : `Checked in ${attendeeView.name}${attendeeView.seat ? ` · Seat ${attendeeView.seat}` : ""}`,
      );
      setTimeout(() => setScan(null), seatFromAttendee(attendeeView) ? 5000 : 1800);
    } finally {
      setSubmittingId("");
    }
  };

  return (
    <div>
      <div className="text-overline">Manual search</div>
      <h1 className="font-heading text-3xl mt-2 tracking-tight">Find and check in</h1>
      <p className="text-sm text-[var(--text-tertiary)] mt-2 max-w-xl">
        Manually check in attendees.
      </p>
      <div className="mt-6 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" aria-hidden />
        <input
          value={q}
          onChange={(ev) => setQ(ev.target.value)}
          placeholder="Search by name, email, company"
          className="input-dark input-dark--with-icon-lg w-full text-base py-4"
          autoFocus
          data-testid="manual-search-input"
        />
      </div>
      <div className="mt-4 surface divide-y divide-[var(--border-default)]">
        {loading && <div className="p-6 text-sm text-[var(--text-tertiary)]">Searching…</div>}
        {!loading &&
          results.slice(0, 12).map((a) => {
            const checkedIn = isAttendeeCheckedIn(a);
            return (
            <div key={a.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:bg-[var(--bg-elev)]/80 transition">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 bg-[var(--bg-elev)] flex items-center justify-center text-xs font-mono shrink-0">{initials(a.name)}</div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.name || "—"}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] truncate">
                    {a.email || "—"} · {a.company || "—"}
                  </div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[var(--primary)]/40 text-[var(--primary)] sm:ml-auto shrink-0">{a.ticket || "—"}</span>
              </div>
              <button
                type="button"
                className={`text-xs justify-center sm:w-auto w-full ${checkedIn ? "btn-secondary opacity-70 cursor-not-allowed" : "btn-primary"}`}
                disabled={checkedIn || submittingId === a.id}
                onClick={() => checkInAttendee(a)}
                data-testid={`manual-checkin-${a.id}`}
              >
                {checkedIn ? "Checked In" : submittingId === a.id ? "Checking…" : "Check in"}
              </button>
            </div>
            );
          })}
        {!loading && results.length === 0 && (
          <div className="p-8 text-center text-sm text-[var(--text-tertiary)]">
            {q.trim() ? "No matches. Try a different query." : "Search by name, email, or company to find invitees."}
          </div>
        )}
      </div>
    </div>
  );
}

function Session({ eventId, setActive }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/sessions`);
        if (!res.ok) throw new Error("sessions");
        const data = await res.json();
        const list = Array.isArray(data.sessions) ? data.sessions : [];
        if (!cancelled) {
          setSessions(list);
          setSelectedId((prev) => prev || (list[0]?.id ?? ""));
        }
      } catch {
        if (!cancelled) setSessions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const selected = sessions.find((s) => s.id === selectedId) || sessions[0];
  const cap = Math.max(1, Number(selected?.capacity || 0));
  const reg = Math.min(cap, Math.max(0, Number(selected?.registered || 0)));
  const pct = Math.min(100, (reg / cap) * 100);

  return (
    <div>
      <div className="text-overline">Session check-in</div>
      <h1 className="font-heading text-3xl mt-2 tracking-tight">Session capacity</h1>
      {loading && <p className="text-sm text-[var(--text-tertiary)] mt-4">Loading schedule…</p>}
      {!loading && sessions.length > 0 && (
        <div className="mt-4">
          <label className="text-overline block">Session</label>
          <select
            className="input-dark mt-2 w-full max-w-lg"
            value={selected?.id || ""}
            onChange={(ev) => setSelectedId(ev.target.value)}
          >
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title || "Untitled"} · {s.time || "—"} · {s.room || "—"}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="mt-8 surface p-8 text-center">
        {!loading && sessions.length === 0 && (
          <p className="text-sm text-[var(--text-secondary)]">No sessions in the schedule yet. Add sessions under Admin → Schedule.</p>
        )}
        {!loading && selected && (
          <>
            <div className="font-heading text-xl text-[var(--text-secondary)] mb-2">{selected.title || "Session"}</div>
            <div className="font-mono text-5xl md:text-7xl">
              {reg.toLocaleString()} / {cap.toLocaleString()}
            </div>
            <div className="text-sm font-mono text-[var(--text-tertiary)] mt-3 uppercase tracking-widest">
              {selected.room ? `${selected.room} · ` : ""}
              {selected.time || "—"}
            </div>
            <div className="mt-6 h-2 bg-[var(--bg-elev)]">
              <div className="h-full bg-[var(--primary)]" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button type="button" className="btn-primary justify-center" onClick={() => setActive("scan")}>
                Scan ticket
              </button>
              <button type="button" className="btn-secondary justify-center" onClick={() => setActive("manual")}>
                Manual
              </button>
              <button type="button" className="btn-secondary justify-center text-[var(--text-secondary)] border-[var(--border-default)]" disabled title="Session lifecycle is managed in admin">
                Info only
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Reprint() {
  return (
    <div>
      <div className="text-overline">Reprint badge</div>
      <h1 className="font-heading text-3xl mt-2 tracking-tight">Reprint queue</h1>
      <div className="mt-6 surface p-8 text-center text-sm text-[var(--text-secondary)]">
        No reprint requests recorded for this event. When reprints are tracked in the system, they will appear here.
      </div>
    </div>
  );
}

function SidePanel({ dashboard, loading, onRefresh, gateLabel, operatorDisplay, deskReady, staffCount }) {
  const checkedIn = Number(dashboard?.checkedIn ?? 0);
  const registered = Number(dashboard?.registered ?? 0);
  const invited = Number(dashboard?.invited ?? 0);
  const remaining = Number(dashboard?.remaining ?? Math.max(0, registered - checkedIn));
  const arrivalRate = registered > 0 ? `${Math.round((checkedIn / registered) * 1000) / 10}%` : "0%";
  const series = Array.isArray(dashboard?.throughputSeries) ? dashboard.throughputSeries : [];
  const lastHour = series.length ? Number(series[series.length - 1]?.v ?? 0) : 0;
  const throughputLabel = lastHour > 0 ? `${lastHour}/hr` : "—";
  const feed = Array.isArray(dashboard?.feed) ? dashboard.feed : [];

  return (
    <>
      <div className="surface p-4 md:p-5">
        <div className="text-overline mb-3">Desk</div>
        <div className="text-sm space-y-2">
          <div className="flex justify-between gap-2">
            <span className="text-[var(--text-tertiary)]">Gate</span>
            <span className="font-mono text-[var(--text-primary)]">{gateLabel}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-[var(--text-tertiary)]">Operator</span>
            <span className={`font-mono truncate max-w-[160px] text-right ${operatorDisplay ? "text-[var(--primary)]" : "text-[var(--text-tertiary)]"}`}>
              {staffCount === 0 ? "—" : operatorDisplay || "Optional"}
            </span>
          </div>
        </div>
      </div>

      <div className="surface p-4 md:p-5">
        <div className="text-overline mb-3 flex items-center justify-between gap-2">
          <span>Live counts</span>
          <button type="button" className="btn-ghost text-[10px] uppercase px-2 py-1 min-h-[36px]" onClick={() => onRefresh?.()} disabled={loading}>
            <RefreshCw size={12} className={loading ? "animate-spin inline" : "inline"} /> Refresh
          </button>
        </div>
        <div className="onsite-stat-grid">
          {[
            ["Checked in", checkedIn.toLocaleString(), true],
            ["Registered", registered.toLocaleString(), false],
            ["Invited", invited.toLocaleString(), false],
            ["Remaining", remaining.toLocaleString(), false],
            ["Arrival", arrivalRate, true],
            ["Throughput", throughputLabel, false],
          ].map(([k, v, accent]) => (
            <div key={k} className="onsite-stat-tile">
              <div className="text-overline">{k}</div>
              <div className={`font-mono text-2xl md:text-3xl mt-1 ${accent ? "text-[var(--primary)]" : "text-[var(--text-primary)]"}`}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="surface p-4 md:p-5 flex-1 min-h-0">
        <div className="text-overline mb-3">Recent check-ins</div>
        <div className="space-y-2 text-xs font-mono max-h-[280px] md:max-h-[360px] overflow-y-auto">
          {feed.length === 0 && <div className="text-[var(--text-tertiary)] py-2">No recent check-ins yet.</div>}
          {feed.map((row, idx) => (
            <div key={`${row.time}-${row.name}-${idx}`} className="flex items-center gap-2 py-2 border-b border-dashed border-[var(--border-default)]">
              <span className="text-[var(--text-tertiary)] w-14 shrink-0">{row.time || "—"}</span>
              <div className="flex-1 min-w-0">
                <div className="truncate text-[var(--text-primary)]">{row.name || "—"}</div>
                <div className="truncate text-[var(--text-tertiary)]">
                  {row.ticket || "—"}
                  {row.seat ? ` · Seat ${row.seat}` : ""}
                </div>
              </div>
              <span className="text-[var(--primary)] shrink-0">✓</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
